import React, { useState } from 'react';
import { ImageUploader } from './ImageUploader';
import { ImageService } from '../services/imageService';
import { ApiService } from '../services/api';
import './CreateBlogPost.css';

interface CreateBlogPostProps {
  onSave?: (blogPost: any) => void;
  onCancel?: () => void;
}

interface BlogPostForm {
  title: string;
  excerpt: string;
  content: string;
  author: string;
  category: string;
  tags: string[];
  imageUrl: string;
  imageAltText: string;
  isPublished: boolean;
  isFeatured: boolean;
  metaDescription: string;
}

const CreateBlogPost: React.FC<CreateBlogPostProps> = ({ onSave, onCancel }) => {
  const [formData, setFormData] = useState<BlogPostForm>({
    title: '',
    excerpt: '',
    content: '',
    author: 'Clement Atangana',
    category: '',
    tags: [],
    imageUrl: '',
    imageAltText: '',
    isPublished: false,
    isFeatured: false,
    metaDescription: ''
  });

  const [tagInput, setTagInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const categories = [
    'Technology',
    'Programming',
    'Web Development',
    'Mobile Development',
    'DevOps',
    'Cloud Computing',
    'AI/ML',
    'Career',
    'Tutorials',
    'Best Practices'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleTagAdd = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleTagRemove = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTagAdd();
    }
  };

  const handleImageUpload = async (imageUrl: string) => {
    setFormData(prev => ({
      ...prev,
      imageUrl: imageUrl,
      imageAltText: formData.imageAltText || formData.title || 'Blog post image'
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.excerpt.trim()) {
      newErrors.excerpt = 'Excerpt is required';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData.author.trim()) {
      newErrors.author = 'Author is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      const blogPost = {
        ...formData,
        readTimeMinutes: Math.ceil(formData.content.split(' ').length / 200), // Estimate reading time
        externalLinks: formData.content.match(/https?:\/\/[^\s]+/g) || [] // Extract links from content
      };

      // Call API to save blog post to Azure Table Storage
      const result = await ApiService.createBlogPost(blogPost);
      
      if (result.success) {
        // Call onSave callback if provided
        if (onSave && result.data) {
          onSave(result.data);
        }

        // Reset form on successful save
        setFormData({
          title: '',
          excerpt: '',
          content: '',
          author: 'Clement Nkamanyi',
          category: '',
          tags: [],
          imageUrl: '',
          imageAltText: '',
          isPublished: false,
          isFeatured: false,
          metaDescription: ''
        });

        alert('Blog post saved successfully to Azure Table Storage!');
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Failed to save blog post:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save blog post. Please try again.';
      alert(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="create-blog-post">
      <div className="container">
        <div className="blog-form-header">
          <h1>Create New Blog Post</h1>
          <p>Share your knowledge and insights with the community</p>
        </div>

        <form onSubmit={handleSubmit} className="blog-form">
          {/* Basic Information */}
          <div className="form-section">
            <h3>Basic Information</h3>
            
            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter blog post title"
                className={errors.title ? 'error' : ''}
                maxLength={100}
              />
              {errors.title && <span className="error-message">{errors.title}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="excerpt">Excerpt *</label>
              <textarea
                id="excerpt"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                placeholder="Brief description of your blog post (will appear in previews)"
                rows={3}
                className={errors.excerpt ? 'error' : ''}
                maxLength={300}
              />
              {errors.excerpt && <span className="error-message">{errors.excerpt}</span>}
              <small>{formData.excerpt.length}/300 characters</small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="author">Author *</label>
                <input
                  type="text"
                  id="author"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  placeholder="Author name"
                  className={errors.author ? 'error' : ''}
                />
                {errors.author && <span className="error-message">{errors.author}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className={errors.category ? 'error' : ''}
                >
                  <option value="">Select a category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                {errors.category && <span className="error-message">{errors.category}</span>}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="form-section">
            <h3>Content</h3>
            
            <div className="form-group">
              <label htmlFor="content">Blog Content *</label>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder="Write your blog post content here... (Supports Markdown)"
                rows={15}
                className={errors.content ? 'error' : ''}
              />
              {errors.content && <span className="error-message">{errors.content}</span>}
              <small>Estimated reading time: {Math.ceil(formData.content.split(' ').length / 200)} minutes</small>
            </div>
          </div>

          {/* Featured Image */}
          <div className="form-section">
            <h3>Featured Image</h3>
            
            <div className="form-group">
              <label>Upload Featured Image</label>
              <ImageUploader
                onImageUploaded={handleImageUpload}
                blogPostId="new-post"
                containerName="blog-images"
              />
            </div>

            {formData.imageUrl && (
              <div className="form-group">
                <label htmlFor="imageAltText">Image Alt Text</label>
                <input
                  type="text"
                  id="imageAltText"
                  name="imageAltText"
                  value={formData.imageAltText}
                  onChange={handleInputChange}
                  placeholder="Describe the image for accessibility"
                />
                <div className="image-preview">
                  <img src={formData.imageUrl} alt="Featured image preview" />
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, imageUrl: '', imageAltText: '' }))}
                    className="remove-image"
                  >
                    Remove Image
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Tags and Metadata */}
          <div className="form-section">
            <h3>Tags & Metadata</h3>
            
            <div className="form-group">
              <label>Tags</label>
              <div className="tag-input-container">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Add a tag and press Enter"
                />
                <button type="button" onClick={handleTagAdd}>Add Tag</button>
              </div>
              {formData.tags.length > 0 && (
                <div className="tags-list">
                  {formData.tags.map((tag, index) => (
                    <span key={index} className="tag">
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleTagRemove(tag)}
                        className="remove-tag"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="metaDescription">Meta Description</label>
              <textarea
                id="metaDescription"
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleInputChange}
                placeholder="SEO meta description (optional)"
                rows={2}
                maxLength={160}
              />
              <small>{formData.metaDescription.length}/160 characters</small>
            </div>
          </div>

          {/* Publishing Options */}
          <div className="form-section">
            <h3>Publishing Options</h3>
            
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isPublished"
                  checked={formData.isPublished}
                  onChange={handleInputChange}
                />
                <span className="checkmark"></span>
                Publish immediately
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isFeatured"
                  checked={formData.isFeatured}
                  onChange={handleInputChange}
                />
                <span className="checkmark"></span>
                Feature this post
              </label>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-secondary"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Blog Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBlogPost;